import { act, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
    format,
    set,
    // parseISO
} from "date-fns";

import DateRange, { IDateRangeProps } from "components/DateRange";
import { IDropdownFilterKeyPair } from "components/Table";
import {
    TActionProps,
    TGeneratePdfFunction,
    TGenerateXLSFunction,
} from "components/Table/components/ActionButtons/types";
import {
    IRangeFilterProps,
} from "components/Table/components/CustomizeFilters/types";
import { ESortOrder, ISortState } from "components/Table/types";
import Tabs from "components/Tabs";
import Typography from "components/Typography";
import apiDateFormat from "constants/apiDateFormat";
import {
    // IDateRange,
    commonDateRangesV2
} from "constants/commonDateRanges";
import { useDebounce } from "hooks";
import styles from "pages/Reporting/styles.module.scss";
// import EDaysOnLot from "types/APIDaysOnLot";
import { IProviderCheckList } from "types/IProviderCheckList";
import ERoutes, { EQueryParameterKeys, ERoutesAnchors } from "types/Routes";
import {
    ELocalSavedFilters,
    ITableWithProviders,
    getDefaultFilters,
    setDefaultFiltersV2,
    tableFiltersWithProviders,
} from "utils/defaultFilters";
import { filterColumnsV2 } from "utils/filterColumns";
import formatOrderingAPI from "utils/formatOrderingAPI";
import {
    EMoneyListTabs,
    getColumns,
    getPdfColumns,
    getXLSColumns,
    tablePDFFileNames,
    tableTabs,
} from "./constants";
import { capitalizeFirstLetter, formattedDateRange, onGeneratePDFDefaultType, reportNaming } from "utils/helper";
import useUrlQuery from "hooks/useUrlQuery";
import { MoneyListUrlParams } from "utils/urlParams";
import { useAppSelector } from "hooks/store";
import { selectUserData } from "store/auth/authSlice";
import DataTable from "components/v2/DataTable";
import { Box, Button, Checkbox, Text } from "@chakra-ui/react";
import { ShowAllInventoryModal } from "./components/ShowAllInventoryModal";
import { ActionButtonV2 } from "components/Table/components/ActionButtons";
import { CustomizeProvider } from "components/v2/CustomizeProvider";
import { ColumnSizingState, PaginationState, RowSelectionState, SortingState, VisibilityState } from "@tanstack/react-table";
import DolSelect from "components/v2/Select/DolSelect";
import usePostUsers, { EUserModules } from "api/usePostUsers";
import useGetReports, { EReportsModules, getReportsAsync, TAdvancedReportOrderKeys, TMoneyListResults } from "api/useGetReports";
import { Navigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import axiosInstance from "services/axiosInstance";
import { ENDPOINTS } from "api/endpoints";
import store from 'store';
import { BiShow } from "react-icons/bi";

interface MoneyListGuardLayoutProps extends React.PropsWithChildren {}

export const MoneyListGuardLayout = ({ children }: MoneyListGuardLayoutProps) => {
    const userAuth = useAppSelector(selectUserData);
    if (userAuth.inventory.value === 'new') {
        return <Navigate to={ERoutes.Home} replace />;
    }
    return (
        <>
            {children}
        </>
    );
};

const MoneyListPage = () => {

    const { mutate: savePagePreference } = usePostUsers<EUserModules.PagePreference>({
        module: EUserModules.PagePreference,
    });

    const { getParam, getMultipleParams, setMultipleParams, removeMultipleParams } = useUrlQuery<MoneyListUrlParams>();

    const startDateParams = getParam("startDate");
    const endDateParams = getParam("endDate");
    const sourceParams = getMultipleParams("source");
    const sortByParams = getParam("sortBy");
    const searchParams = getParam("search");
    const activeTableParams = getParam("activeTable") || EMoneyListTabs.Conservative;
    // const leadsZeroParams = getParam("leadsZero");

    const currentTableFilter = useMemo((): ELocalSavedFilters | null => {
        switch (activeTableParams as EMoneyListTabs) {
            case EMoneyListTabs.Aggressive:
                return ELocalSavedFilters.AggressiveTable;

            case EMoneyListTabs.Conservative:
                return ELocalSavedFilters.ConservativeTable;
            case EMoneyListTabs.Prioritize:
                return ELocalSavedFilters.PrioritizedTable;
            default:
                return null;
        }
    }, [activeTableParams]);

    const defaultFilters = useMemo(() => {
        if (currentTableFilter) {
            return getDefaultFilters<ITableWithProviders>(
                currentTableFilter,
                tableFiltersWithProviders
            );
        }

        return tableFiltersWithProviders;
    }, [currentTableFilter]);

    const [dateRange, setDateRange] = useState<IDateRangeProps["value"]>(
        startDateParams && endDateParams ? {
            startDate: new Date(startDateParams),
            endDate: new Date(endDateParams),
        } : commonDateRangesV2[2].value
    );
    
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(defaultFilters.pageSize);

    const [providers, setProviders] = useState<IProviderCheckList[]>(
        // defaultFilters.providers
        []
    );

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        defaultFilters.columnVisibility && Object.keys(defaultFilters.columnVisibility).length > 0 ? defaultFilters.columnVisibility : { vin: false }
    );

    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(defaultFilters.columnSizing || {});

    useEffect(() => {
        if (defaultFilters.pageSize) {
            setPageSize(defaultFilters.pageSize);
        }
        if (defaultFilters.columnVisibility && Object.keys(defaultFilters.columnVisibility).length > 0) {
            setColumnVisibility(defaultFilters.columnVisibility);
        }
        if (defaultFilters.columnSizing) {
            setColumnSizing(defaultFilters.columnSizing || {});
        }
        
    }, [activeTableParams]);

    // const dolOptions: IDropdownFilterKeyPair[] = useMemo(() => [
    //     {
    //         label: 'None',
    //         value: '0',
    //     },
    //     {
    //         label: 'Over 7',
    //         value: '7',
    //     },
    //     {
    //         label: 'Over 15',
    //         value: '15',
    //     },
    //     {
    //         label: 'Over 30',
    //         value: '30',
    //     },
    //     {
    //         label: 'Over 60',
    //         value: '60',
    //     },
    // ], []);


    // const findOption = selectedDolRangeParams ? dolOptions.find((option) => option.value === parseInt(selectedDolRangeParams)) : dolOptions[0];

    // const [selectedDol, setSelectedDol] = useState<IDropdownFilterKeyPair>(findOption ?? dolOptions[0]);

    const [sortBy, setSortBy] = useState<SortingState>(sortByParams && typeof sortByParams === 'string' ? [{
        id: sortByParams.replace("-", ""),
        desc: sortByParams.startsWith("-") ? true : false,
    }] : []);

    const [quickSearch, setQuickSearch] = useState<string>(searchParams ?? "");

    // const [dolRange, setDolRange] = useState<IRangeFilterProps["value"]>(
    //     dolRangeParams
    //         ? (dolRangeParams.split("-").map(Number) as [number, number])
    //         : [1, 2000]
    // );

    const quickSearchApi = useDebounce<string>(quickSearch, 350);

    const userAuth = useAppSelector(selectUserData);

    useEffect(() => {
        const updatedProviders = [
            ...userAuth.dealerV2.third_party_provider
                .filter((provider) => provider.key === 'CGR')
                .map((provider) => {
                    const defaultProvider = defaultFilters.providers.find(({ id }) => id === provider.key);
                    return {
                        id: provider.key,
                        label: provider.label,
                        // value: userAuth.dealerV2.is_lite ? true : defaultProvider ? defaultProvider.value : false// Default to false for other providers
                        value: true, // Default to true for CGR
                    };
                }),
        ] as IProviderCheckList[];

        setProviders(updatedProviders);
    }, [userAuth.dealerV2.third_party_provider, defaultFilters]);

    // useMemo(() => {

    //     const providers = userAuth.dealerV2.third_party_provider.map((provider) => ({
    //         id: provider.key,
    //         label: provider.label,
    //         value: sourceParams?.includes(provider.key) || false,
    //     })) as IProviderCheckList[]; 

    //     setProviders(providers);

    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [userAuth.dealerV2.uid]);

    const sortedBy: ISortState = useMemo(() => {

        if (!sortBy.length) {
            return {
                column: null,
                order: ESortOrder.None,
            };
        }
        return {
            column: sortBy[0].id as TAdvancedReportOrderKeys,
            order: sortBy[0].desc ? ESortOrder.Descending : ESortOrder.Ascending,
        };
    }, [sortBy]);

    const fetchParameters = useMemo(
        () => ({
            module: EReportsModules.MoneyList,
            params: {
                date_from: format(dateRange.startDate, apiDateFormat),
                date_to: format(dateRange.endDate, apiDateFormat),
                dealership_uid: userAuth.dealerV2.uid,
                page: page + 1,
                page_size: pageSize,
                ordering: formatOrderingAPI<TAdvancedReportOrderKeys>(sortedBy),
                search: quickSearchApi,
                source: providers.filter(({ value }) => value).map(({ id }) => id),
                inventory: userAuth.inventory.value,
                report_type: activeTableParams ?? EMoneyListTabs.Conservative,
            },
        }),
        [
            // selectedDol.value,
            activeTableParams, 
            // dolRangeParams, 
            dateRange.startDate, 
            dateRange.endDate, 
            userAuth.dealerV2.uid, 
            userAuth.inventory.value, 
            page, 
            pageSize, 
            sortedBy, 
            quickSearchApi, 
            providers
        ]
    );

    const { data, isFetching, refetch } = useGetReports<EReportsModules.MoneyList>(fetchParameters);

    useEffect(() => {
        if (!data?.results) return;

        // Only include VINs present in the current data
        const checkedData: RowSelectionState = {};
        data.results.forEach(item => {
            if (item.prioritize) {
                checkedData[item.vin] = true;
            }
        });

        setRowSelection(checkedData);
    }, [data?.results, activeTableParams]);

    useEffect(() => {
        // update url

        setMultipleParams({
            startDate: format(dateRange.startDate, apiDateFormat),
            endDate: format(dateRange.endDate, apiDateFormat),
            activeTable: activeTableParams ?? undefined,
            // ...(quickSearchApi && {
            //   search: quickSearchApi
            // }),
            // ...(sortBy.order && sortBy.column && {
            //   sortBy: sortBy.order === ESortOrder.Ascending ? sortBy.column : `-${sortBy.column}`,
            // }),
            // ...(activeTableParams === EMoneyListTabs.Aggressive && selectedDol.value === 0 && {
            //     dolRange: `${dolRange[0]}-${dolRange[1]}`
            // }),
            // ...(leadsZeroParams && {
            // leadsZero: leadsZeroParams ?? undefined,
            // }),
            // ...(activeTableParams === EMoneyListTabs.DaysOnLot && selectedDol.value !== 0 && {
            // selectedDolRange: selectedDol ? selectedDol.value.toString() : undefined,
            // })
            inventory: userAuth.inventory.value,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange.startDate, dateRange.endDate, activeTableParams, userAuth.inventory.value]);

    const onChangerOrder = useCallback((newTabIndex: number, newOrder?: string | number) => {

        // setDateRange(newOrder === EMoneyListTabs.Aggressive ? commonDateRangesV2[2].value : commonDateRangesV2[2].value);

        console.log('newOrder', newOrder);

        // dispatch(setActiveTab(newOrder as EMoneyListTabs));

        removeMultipleParams(['sortBy']);

        // setSelectedDol(dolOptions[0]);

        // setDolRange([1, 2000]);

        setMultipleParams({
            [EQueryParameterKeys.ActiveTable]: newOrder as EMoneyListTabs,
        });

        setPage(0);

        setSortBy([]);
        
        refetch();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = (search: string) => {
        setQuickSearch(search);
        setPage(0);
        setMultipleParams({
            search
        });
    };

    const onSort = useCallback((sort: SortingState) => {
        // if (!sort.length) return;
        setSortBy(sort);
        setMultipleParams({
            sortBy: sort.length > 0 ? `${sort[0].desc ? '-' : ''}${sort[0].id}` : '',
        });
    }, []);

    const activeIndexQuickOption = useMemo(
        () =>
            commonDateRangesV2.findIndex(
                ({ value: quickOptionValue }) =>
                    format(quickOptionValue.startDate, apiDateFormat) ===
                    format(dateRange.startDate, apiDateFormat) &&
                    format(quickOptionValue.endDate, apiDateFormat) ===
                    format(dateRange.endDate, apiDateFormat)
            ),
        [dateRange.endDate, dateRange.startDate]
    );

    const onGeneratePDF: TActionProps<TGeneratePdfFunction> = useCallback(
        async (onGeneratePDF, setIsLoading) => {
            setIsLoading(true);

            try {
                let hasNextPage = true;
                let collectedResults: TMoneyListResults[] = [];
                // let mapOffset = defaultOffset;

                let tempPage = 1;

                do {
                    const { next, results } = await getReportsAsync<EReportsModules.MoneyList>({
                        ...fetchParameters,
                        params: {
                            ...fetchParameters.params,
                            page: tempPage,
                            page_size: 1000
                            // offset: mapOffset,
                        },
                    })();

                    collectedResults = [...collectedResults, ...results];

                    tempPage++;
                    // console.log('next', next);

                    hasNextPage = next ? true : false;
                    // mapOffset = mapOffset + defaultPageSize;
                } while (hasNextPage);


                // filter providers
                const filteredProviders = providers.filter(({ value }) => value).map((providers) => {
                    // cgr_deal_rating
                    // atc_deal_rating
                    // ccm_deal_rating
                    return providers.id.toLowerCase();
                });

                // in columnVisibility include vin even not exist of false
                if (!columnVisibility.vin) {
                    columnVisibility.vin = true;
                }

                // create a filter in pdfColumns to filter out the columns that are not in the filteredProviders with a same prefix in the key deal_rating then all other key should include without the deal_rating
                const filteredPDFColumns = getPdfColumns(userAuth.inventory.value, userAuth.dealerV2, columnVisibility).filter(({ key }) => {
                    if (key.includes('_deal_rating')) {
                        return filteredProviders.includes(key.split('_deal_rating')[0]);
                    }
                    return true;
                });

                const bodyFormatted = collectedResults.map((values) => {
                    return filteredPDFColumns.map(
                        ({ func, key }) =>
                            func?.(values[key], values) || (values[key] as string)
                    );
                });

                const dateRangeTitle = activeIndexQuickOption !== -1 ? `${commonDateRangesV2[activeIndexQuickOption].label} (${formattedDateRange(dateRange)})` : formattedDateRange(dateRange);

                const tablName = tableTabs.find((tableTab) => tableTab.id === activeTableParams)!.label;

                const filename = reportNaming({
                    dealersshipName: userAuth.dealerV2.name,
                    report: `${capitalizeFirstLetter(tablePDFFileNames[activeTableParams as EMoneyListTabs])} List`,
                    inventory: userAuth.inventory.value,
                    extension: 'pdf'
                });
                
                onGeneratePDF(
                    {
                        head: [filteredPDFColumns.map(({ label }) => label)],
                        body: bodyFormatted,
                        ...onGeneratePDFDefaultType(`${capitalizeFirstLetter(tablName)}`, dateRangeTitle)
                    },
                    filename,
                    {
                        orientation: "landscape",
                        compress: true
                    }
                );
                setIsLoading(false);
            } catch (err) {
                toast.error("Can't export PDF file. Please contact your admin");
            }
        },
        [providers, userAuth.inventory.value, userAuth.dealerV2, activeIndexQuickOption, dateRange, activeTableParams, fetchParameters, columnVisibility]
    );

    const onGenerateXLSX: TActionProps<TGenerateXLSFunction> = useCallback(
        async (generateXLS, setIsLoading) => {
            setIsLoading(true);

            try {
                let hasNextPage = true;
                let collectedResults: TMoneyListResults[] = [];
                // let mapOffset = defaultOffset;

                let tempPage = 1;

                do {
                    const { next, results } = await getReportsAsync<EReportsModules.MoneyList>({
                        ...fetchParameters,
                        params: {
                            ...fetchParameters.params,
                            page: tempPage,
                            page_size: 1000
                            // offset: mapOffset,
                        },
                    })();

                    collectedResults = [...collectedResults, ...results];

                    tempPage++;
                    // console.log('next', next);

                    hasNextPage = next ? true : false;
                    // mapOffset = mapOffset + defaultPageSize;
                } while (hasNextPage);


                // filter providers
                const filteredProviders = providers.filter(({ value }) => value).map((providers) => {
                    return providers.id.toLowerCase();
                });

                // in columnVisibility include vin even not exist of false
                if (!columnVisibility.vin) {
                    columnVisibility.vin = true;
                }

                // create a filter in pdfColumns to filter out the columns that are not in the filteredProviders with a same prefix in the key deal_rating then all other key should include without the deal_rating
                const filteredXLSColumns = getXLSColumns(userAuth.inventory.value, userAuth.dealerV2, columnVisibility).filter(({ key }) => {
                    if (key.includes('_deal_rating')) {
                        return filteredProviders.includes(key.split('_deal_rating')[0]);
                    }
                    return true;
                });

                const bodyFormatted = collectedResults.map((values) => {
                    return filteredXLSColumns.map(
                        ({ func, key }) =>
                            func?.(values[key], values) || (values[key] as string)
                    );
                });

                const tablName = tableTabs.find((tableTab) => tableTab.id === activeTableParams)!.label;

                const dateRangeTitle = activeIndexQuickOption !== -1 ? `${commonDateRangesV2[activeIndexQuickOption].label} (${formattedDateRange(dateRange)})` : formattedDateRange(dateRange);

                const filename = reportNaming({
                    dealersshipName: userAuth.dealerV2.name,
                    report: `${capitalizeFirstLetter(tablePDFFileNames[activeTableParams as EMoneyListTabs])} List`,
                    inventory: userAuth.inventory.value,
                    extension: 'xlsx'
                });

                generateXLS(
                    {
                        head: [filteredXLSColumns.map(({ label }) => label)],
                        body: bodyFormatted,
                        title: capitalizeFirstLetter(tablName),
                        formattedDate: dateRangeTitle
                    },
                    filename
                );
                setIsLoading(false);
            } catch (err) {
                toast.error("Can't export XLS file. Please contact your admin");
            }
        },
        [providers, userAuth.inventory.value, userAuth.dealerV2, activeTableParams, fetchParameters, columnVisibility]
    );

    let filteredColumns = useMemo(
        () => filterColumnsV2(getColumns(userAuth.inventory.value, userAuth.dealerV2), providers),
        [providers, userAuth.dealerV2, userAuth.inventory.value]
    );

    const [columnPositions, setColumnPositions] = useState<string[]>(
        defaultFilters.columnPositions || filteredColumns.map((column) => column.id!)
    );

    // if data in results has no default_percentage_to_market column remove that column in filteredColumns
    if (data?.results && data.results.length > 0) {
        const hasDefaultPercentageToMarket = data.results.some(result => result.default_percentage_to_market !== undefined);
        if (!hasDefaultPercentageToMarket) {
            filteredColumns = filteredColumns.filter(column => column.id !== 'default_percentage_to_market');
        }
    }

    const onPageChange = useCallback((pagination: PaginationState) => {
        setPage(pagination.pageIndex);
        setPageSize(pagination.pageSize);
    }, []);

    const onDateChange = (newValue: [Date, Date]) => {
        // setDateRange({ startDate, endDate });
        setDateRange({
            startDate: set(newValue[0], { hours: 0, minutes: 0, seconds: 0 }),
            endDate: set(newValue[1], { hours: 23, minutes: 59, seconds: 59 }),
        });
    };

    const onProviderChange = (selectedProvider: IProviderCheckList) => {
        setProviders((prevProviders) =>
            prevProviders.map(provider =>
                provider.id === selectedProvider.id ? { ...provider, value: !provider.value } : provider
            )
        );

        setSortBy([]);

    };

    // const onDolChange = (selectedDol: IDropdownFilterKeyPair) => {
    //     setSelectedDol(selectedDol);
    //     // setSortBy([{ id: 'dol', desc: false }]);
    // };

    const onSavePreferences = () => {

        if (!currentTableFilter) return;

        const pagePrefrence = setDefaultFiltersV2<ITableWithProviders>(currentTableFilter, {
            dateRange,
            pageSize,
            providers,
            columnVisibility,
            columnSizing,
            columnPositions
        });

        console.log(JSON.parse(pagePrefrence));

        savePagePreference({
            settings: JSON.parse(pagePrefrence),
        }, {
            onError: () => {
                toast.error("Failed to save preferences");
            },
            onSuccess: () => {
                toast.success("Page preferences saved successfully");
            }
        });
    };

    const getActiveTable = tableTabs.findIndex((tableTab) => tableTab.id === activeTableParams);
    
    const handleRowSelectionChange = (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
        
        if (typeof updaterOrValue === "function") {
            const result = updaterOrValue(rowSelection); // or use 'prev' if inside setRowSelection
        console.log('updaterOrValue is a function, result:', result);
        } else {
            console.log('updaterOrValue is an object:', updaterOrValue);
        }
        
        
        setRowSelection((prev) => {
            const newSelection: RowSelectionState =
                typeof updaterOrValue === "function"
                    ? (updaterOrValue as (old: RowSelectionState) => RowSelectionState)(prev)
                    : updaterOrValue;

            const merged = { ...prev };

            // Keep previous keys and mark them false if they are missing from newSelection
            Object.keys(prev).forEach((key) => {
                if (!(key in newSelection)) {
                    merged[key] = false;
                }
            });

            // Overwrite/add keys from the new selection (these are the currently selected ones)
            Object.entries(newSelection).forEach(([key, value]) => {
                merged[key] = value;
            });
            
            return merged;
        });
    };

    const onSavePrioritize = async () => {
        
        // get keys with true
        const selectedVins = Object.entries(rowSelection)
            .filter(([_, v]) => v === true)
            .map(([vin]) => vin);
        
        // get keys with explicit false
        const unselectedVins = Object.entries(rowSelection)
        .filter(([_, v]) => v === false)
        .map(([vin]) => vin);
        
        const state = store.getState();
        const token = state.auth.accessToken;

        const savePriorities = await axiosInstance.post(ENDPOINTS.PRIORITIZE_VEHICLES, {
            dealership_uid: userAuth.dealerV2.uid,
            added_vins: selectedVins,
            removed_vins: unselectedVins,
        },
        {
            headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            },
        });

        if (savePriorities.status !== 201) {
            toast.error("Failed to save prioritized vehicles");
            return;
        }
        
        // const filteredSelection: RowSelectionState = Object.fromEntries(
        //     Object.entries(rowSelection || {}).filter(([_, v]) => v === true)
        // );
            
        // setRowSelection(rowSelection);
        
        console.log('rowSelection', rowSelection);
        
        // Refresh data after saving priorities
        refetch();
        
        // show notification for added and removed prioritized vehicles
        const message = [];
        if (selectedVins.length > 0) {
            message.push(`Added ${selectedVins.length} vehicles`);
        }
        
        if (unselectedVins.length > 0) {
            message.push(`Removed ${unselectedVins.length} vehicles`);
        }
        
        toast.success(`${message.join(", ")} to prioritize list successfully`);
    };
    
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);

    const onShowInventory = () => {
        setIsInventoryModalOpen(true);
    };

    const controller = () => {
        return (
            <Box
                display="flex"
                gap="15px"
                justifyContent="space-between"
                flex="1"
                alignItems="center"
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap="15px"
                    flex="1"
                >
                    
                    {currentTableFilter === ELocalSavedFilters.PrioritizedTable && (
                        <Button
                            rightIcon={<BiShow color="#57adca" fontSize="large" />}
                                    size="md"
                            border="1px solid #b3b8db"
                            backgroundColor="#ffffff"
                            fontSize="14px"
                            padding="10px 16px"
                            // height="43px"
                            // onClick={() => onClickPrint(undefined)}
                            loadingText="Saving..."
                            spinnerPlacement="end"
                            onClick={onShowInventory}
                            type="button"
                        >
                            Show all Inventory
                        </Button>
                    )}
                    <Button
                        rightIcon={<FiPlus color="#57adca" fontSize="large" />}
                                  size="md"
                        border="1px solid #b3b8db"
                        backgroundColor="#ffffff"
                        fontSize="14px"
                        padding="10px 16px"
                        // height="43px"
                        // onClick={() => onClickPrint(undefined)}
                        loadingText="Saving..."
                        spinnerPlacement="end"
                        isDisabled={Object.keys(rowSelection).length === 0}
                        onClick={onSavePrioritize}
                        type="button"
                    >
                        Save Prioritize
                    </Button>
                </Box>
                <Box>
                    <ActionButtonV2
                        actionConfig={{
                            isDownloadPDF: false,
                            isDownloadXLS: true,
                            isEmailPDF: false,
                            isEmailXLS: true,
                            isPrintPDF: false,
                        }}
                        // onDownloadPDF={onGeneratePDF}
                        onDownloadXLS={onGenerateXLSX}
                        // onGeneratePDF={onGeneratePDF}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <>
        <Box
            id={ERoutesAnchors.ReportingTables}
            display="flex"
            flexDirection="column"
            flex={1}
        >
            {/* <Typography
                className={styles["reporting__tableLabel"]}
                fontSize="text-xl"
                fontWeight="bold"
            >
                Money List
            </Typography> */}
            <Box
                display="flex"
                flexDirection="column"
                gap="15px"
                flex={1}
            >
                <Tabs
                    active={getActiveTable}
                    tabs={tableTabs}
                    onClickTab={onChangerOrder}
                />
                {/* {activeTableParams !== EMoneyListTabs.VDPBoost && ( */}

                <Box
                    display="flex"
                    flexDirection="column"
                    gap="15px"
                    flex={1}
                >
                    {/* <DateRange
                        onChange={onDateChange}
                        quickOptions={commonDateRangesV2}
                        value={dateRange}
                    /> */}
                    <DataTable<TMoneyListResults>
                        data={data?.results || []}
                        rowCount={data?.count || 0}
                        onPageChange={onPageChange}
                        sort={sortBy}
                        onSort={onSort}
                        columns={filteredColumns as any}
                        pageIndex={page}
                        pageSize={pageSize}
                        isLoading={isFetching}
                        conttroller={controller()}
                        search={quickSearchApi}
                        onSearch={onSearch}
                        pageCount={data?.total_pages}
                        onColumnVisibilityChange={setColumnVisibility}
                        columnVisibility={columnVisibility}
                        onColumnSizingChange={setColumnSizing}
                        columnSizing={columnSizing}
                        showColumns={true}
                        // columnsSidebarChildren={!userAuth.dealerV2.is_lite ? columnSidebarChilrend() : undefined}
                        // onUpdateData={updateData}
                        onSavePreferences={onSavePreferences}
                        showTotal={true}
                        label="Vehicles"
                        columnPinning={{
                            left: ['prioritize', 'stock_number']
                        }}
                        columnPositions={columnPositions}
                        onColumnOrderChange={setColumnPositions}
                        enableMultiRowSelection={true}
                        enableRowSelection={true}
                        onRowSelectionChange={handleRowSelectionChange}
                        rowSelection={rowSelection}
                        getRowId={(row) => row.vin}
                    />
                </Box>
            </Box>
        </Box>

        {isInventoryModalOpen && (
            <ShowAllInventoryModal
                isOpen={isInventoryModalOpen}
                onClose={() => setIsInventoryModalOpen(false)}
                dateRange={dateRange}
                providers={providers}
                onSaved={refetch}
            />
        )}
        </>
    );
};

export default MoneyListPage;
